import { useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { getRouteApi } from '@tanstack/react-router';

import ClearFilters from '@/components/blocks/data-table/clear-filters';
import DatasetCombobox from '@/components/dataset-combobox';
import ModelsCombobox from '@/components/models-combobox';
import TaskCombobox from '@/components/task-combobox';
import DatePicker from '@/components/ui/date-picker';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Switch } from '@/components/ui/switch';
import UserCombobox from '@/components/user-combobox';
import { useFormSearchParams } from '@/hooks/use-form-search-params';
import type { ClusterFiltersSchema } from '@/routes/(app)/(clusters)/lib/schema';

const route = getRouteApi('/app/clusters');

const ClusterFilters = () => {
  const { t } = useTranslation();
  const { task } = route.useSearch();
  const form = useFormContext<ClusterFiltersSchema>();
  const allValues = form.watch();

  useFormSearchParams('/app/clusters');

  const amountOfActiveFilters = useMemo(
    () =>
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      Object.values(allValues).filter((value) => value !== undefined).length,
    [allValues]
  );

  const handleClearFilters = useCallback(() => {
    form.reset({
      task: undefined,
      createdBy: undefined,
      datasetId: undefined,
      date: undefined,
      models: undefined,
      isEval: true,
    });
  }, [form]);

  return (
    <div className='flex flex-col gap-4'>
      <div className='grid grid-cols-3 gap-x-4 gap-y-2'>
        <FormField
          control={form.control}
          name='task'
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('clusters.filters.task')}</FormLabel>
              <FormControl>
                <TaskCombobox {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name='datasetId'
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('clusters.filters.dataset')}</FormLabel>
              <FormControl>
                <DatasetCombobox
                  task={task}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name='models'
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('clusters.filters.models')}</FormLabel>
              <FormControl>
                <ModelsCombobox
                  task={task}
                  {...field}
                  values={field.value}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name='date'
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('clusters.filters.date')}</FormLabel>
              <FormControl>
                <DatePicker
                  {...field}
                  mode='range'
                  selected={{
                    from: field.value?.from
                      ? new Date(field.value.from)
                      : undefined,
                    to: field.value?.to ? new Date(field.value.to) : undefined,
                  }}
                  onSelect={(range) => {
                    field.onChange({
                      from: range?.from ?? undefined,
                      to: range?.to ?? undefined,
                    });
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name='createdBy'
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('clusters.filters.createdBy')}</FormLabel>
              <FormControl>
                <UserCombobox {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name='isEval'
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('clusters.filters.isEval')}</FormLabel>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
      <ClearFilters
        amountOfActiveFilters={amountOfActiveFilters}
        onClearFilters={handleClearFilters}
      />
    </div>
  );
};

export default ClusterFilters;
